import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ArtistPortalEarningsGroup } from 'fym-shared/src/smartlinks/artistPortals';
import { StreamingOverviewResponse } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { formatDay, formatMonth } from '../streamingFormat';
import { sectionGivenBy, sectionSources, sourceNames } from '../streamingSources';
import { streamingEarningsMessages as messages } from '../streamingEarningsMessages';
import { StreamingBlock } from '../parts/StreamingBlock';
import { EarningsSummaryBody } from '../parts/EarningsSummaryBody';
import { EarningsGroupBlock } from '../parts/EarningsGroupBlock';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface EarningsSectionProps {
  accountId: number;
  overview: StreamingOverviewResponse;
  onOpenCatalog: () => void;
}

/**
 * Streaming → Earnings (design e1; a member sees e2; no source with earnings e3; Free sees the plan
 * card, `hiddenReason: 'plan'`). The amounts come
 * from every connected source that reports earnings. The amounts of each currency are apart.
 * Music24 reads no balance, no payout and no bank data.
 */
const EarningsSectionComponent: React.FC<EarningsSectionProps> = ({ accountId, overview, onOpenCatalog }) => {
  const intl = useIntl();
  const { earnings } = overview;
  const earningSources = useMemo(() => sectionSources(overview, 'earnings'), [overview]);
  const showDetail = !earnings.hidden && earnings.connected && earnings.months !== null;
  const { months } = earnings;
  // The oldest last read of the sources: every amount is at least that fresh.
  const lastRead = useMemo(
    () =>
      overview.sources
        .filter((source) => earningSources.includes(source.integrationType))
        .map((source) => source.lastSyncAt)
        .filter((value): value is string => value !== null)
        .sort()[0] ?? null,
    [earningSources, overview.sources]
  );

  return (
    <>
      <StreamingBlock title={intl.formatMessage(messages.inThisPeriod)} testId="streaming-section-earnings">
        {showDetail && months !== null ? (
          <p className={styles.footnote}>
            {intl.formatMessage(messages.report, {
              source: sourceNames(earningSources, 'shortName'),
              start: formatMonth(intl, months.from),
              end: formatMonth(intl, months.to),
              date: lastRead === null ? '—' : formatDay(intl, lastRead.slice(0, 10)),
            })}
          </p>
        ) : null}
        <EarningsSummaryBody
          earnings={earnings}
          givenBy={sectionGivenBy(overview, 'earnings')}
          onOpenCatalog={onOpenCatalog}
        />
      </StreamingBlock>
      {showDetail && months !== null && (earnings.amounts ?? []).length > 0 ? (
        <>
          <EarningsGroupBlock accountId={accountId} groupBy={ArtistPortalEarningsGroup.period} months={months} />
          <div className={styles.blocksTwo}>
            <EarningsGroupBlock accountId={accountId} groupBy={ArtistPortalEarningsGroup.store} months={months} />
            <EarningsGroupBlock accountId={accountId} groupBy={ArtistPortalEarningsGroup.track} months={months} />
          </div>
          <EarningsGroupBlock
            accountId={accountId}
            groupBy={ArtistPortalEarningsGroup.country}
            months={months}
            hideWhenEmpty
          />
          <p className={styles.footnote}>{withBrandWord(intl.formatMessage(messages.noBank))}</p>
        </>
      ) : null}
    </>
  );
};

export const EarningsSection = TypedMemo(EarningsSectionComponent);
